import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Sparkles, Upload, X, File } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useParserSandbox } from '@/hooks/useParserSandbox';
import { ParserSupplierSelect } from '@/components/parser/ParserSupplierSelect';
import { ExtractedDataDisplay } from '@/components/parser/ExtractedDataDisplay';
import { ParserPortalRequestModal } from '@/components/parser/ParserPortalRequestModal';
import { transformParsed, type BillHeader, type LineItem } from '../../utils/parserTransform';
import { exportSandboxDataToCsv } from '@/services/api/parser';
import { ParseProgress } from '@/components/parser/ParseProgress';
import { ReactPDFPreview } from '@/components/parser/ReactPDFPreview';

export default function ParserSandbox() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [parserConfig] = useState({
    parserType: 'ai' as 'ai' | 'rule' | 'hybrid',
    ocrEngine: 'tesseract' as 'tesseract' | 'google-vision',
    confidenceThreshold: 0.5,
    customRules: {}
  });
  
  const [parsedData, setParsedData] = useState<any>(null);
  const [canonicalData, setCanonicalData] = useState<{ header: BillHeader; lineItems: LineItem[] } | null>(null);
  // Use mutation state instead of a separate isLoading flag to avoid double-clicks & race conditions
  const [isLoading, setIsLoading] = useState(false);
  const [parseProgress, setParseProgress] = useState({ percent: 0, stage: '' });
  // Single preview implementation
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const { testParse } = useParserSandbox();
  const [lastParseMessage, setLastParseMessage] = useState<string | null>(null);
  const [lastParseStatus, setLastParseStatus] = useState<'success' | 'error' | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({ title: 'Invalid file type', description: 'Please upload a PDF file', variant: 'destructive' });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      setSelectedFile(file);
    } else {
      toast({ title: 'Invalid file type', description: 'Please upload a PDF file', variant: 'destructive' });
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isDragOver) setIsDragOver(false);
  };

  // Track created timeouts so we can clear them on completion/error to prevent lingering modal
  let progressTimeouts: number[] = [];

  const clearProgressTimeouts = () => {
    for (const id of progressTimeouts) {
      clearTimeout(id);
    }
    progressTimeouts = [];
  };

  const handleParseData = async () => {
    if (!selectedFile) {
  toast({ title: 'Missing file', description: 'Please upload a file', variant: 'destructive' });
      return;
    }

    if (!selectedSupplier) {
  toast({ title: 'Missing supplier', description: 'Please select a supplier', variant: 'destructive' });
      return;
    }

    // Guard against rapid re-clicks while mutation is active
  if (testParse.isPending) return;
  setLastParseMessage(null);
  setLastParseStatus(null);
    setIsLoading(true);
    setParseProgress({ percent: 10, stage: 'connecting' });
    
    try {
      // Staged progress simulation
      progressTimeouts.push(window.setTimeout(() => setParseProgress({ percent: 20, stage: 'authenticating' }), 300));
      progressTimeouts.push(window.setTimeout(() => setParseProgress({ percent: 40, stage: 'processing' }), 600));
      progressTimeouts.push(window.setTimeout(() => setParseProgress({ percent: 60, stage: 'extracting' }), 1200));
      progressTimeouts.push(window.setTimeout(() => setParseProgress({ percent: 90, stage: 'analyzing' }), 2000));

      const result = await testParse.mutateAsync({
        file: selectedFile,
        supplierName: selectedSupplier.shortName || selectedSupplier.name,
        templateId: '1',
        parserType: parserConfig.parserType,
        ocrEngine: parserConfig.ocrEngine,
        confidenceThreshold: parserConfig.confidenceThreshold,
        customRules: parserConfig.customRules
      });

      // Transform to canonical format
      const transformed = transformParsed(
        result,
        selectedSupplier.name,
        parserConfig.confidenceThreshold
      );
      
      clearProgressTimeouts();
      setParseProgress({ percent: 100, stage: 'complete' });
  setParsedData(result);
  setCanonicalData(transformed);

  // Prefer backend-sent message if present
      const successMsg = (result as any)?.__message || (result as any)?.message || 'Parsing complete!';
      setLastParseMessage(successMsg);
      setLastParseStatus('success');
      toast({ title: 'Parse Complete', description: successMsg });
    } catch (error: any) {
      clearProgressTimeouts();
      setLastParseMessage(error.message || 'Parsing failed');
      setLastParseStatus('error');
      toast({ title: 'Parse Failed', description: error.message || 'Parsing failed', variant: 'destructive' });
    } finally {
      // On error, close immediately; on success allow a short grace period for user to see 100%
      const delay = testParse.isError ? 0 : 400;
      window.setTimeout(() => {
        setIsLoading(false);
        setParseProgress({ percent: 0, stage: '' });
      }, delay);
    }
  };

  const handleCancelParse = () => {
    clearProgressTimeouts();
    setIsLoading(false);
    setParseProgress({ percent: 0, stage: '' });
  toast({ title: 'Parsing cancelled', description: 'You stopped the current parsing operation.' });
  };

  // Remove fields that are null (recursively) before exporting to CSV
  const pruneNullsDeep = (value: any): any => {
    if (Array.isArray(value)) {
      return value
        .map(pruneNullsDeep)
        .filter((v) => v !== null && v !== undefined);
    }
    if (value && typeof value === 'object') {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(value)) {
        const cleaned = pruneNullsDeep(v);
        if (cleaned !== null && cleaned !== undefined) {
          out[k] = cleaned;
        }
      }
      return out;
    }
    return value;
  };

  const handleClear = () => {
    setSelectedFile(null);
    setParsedData(null);
    setCanonicalData(null);
    setLastParseMessage(null);
    setLastParseStatus(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-6 max-w-[1800px]">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            Parser Sandbox
          </h1>
          <p className="text-slate-600">
            Test and validate AI-powered bill parsing in real-time
          </p>
        </div>

        <Alert className="mb-4 border-blue-200 bg-blue-50">
          <Sparkles className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-slate-700">
            Upload a bill document to test AI parsing. No data is saved to the database.
          </AlertDescription>
        </Alert>

        {lastParseMessage && (
          <Alert className={`mb-4 ${lastParseStatus === 'error' ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
            <AlertDescription className="text-sm">
              {lastParseMessage}
            </AlertDescription>
          </Alert>
        )}

        {/* Compact Configuration Card with aligned labels row and inputs row */}
        <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-blue-50/30 mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              {/* Row 1: Labels (desktop only) */}
              <div className="hidden md:contents">
                <div className="md:col-span-3"><Label className="text-xs block h-5 leading-5">Supplier *</Label></div>
                <div className="md:col-span-2"><Label className="text-xs block h-5 leading-5">Parser Type</Label></div>
                <div className="md:col-span-4"><Label className="text-xs block h-5 leading-5">Upload Bill *</Label></div>
                <div className="md:col-span-3"><span className="block h-5 leading-5 text-xs opacity-0">Actions</span></div>
              </div>

              {/* Row 2: Inputs */}
              {/* Supplier Selection */}
              <div className="md:col-span-3">
                {/* Fallback label for mobile */}
                <Label className="text-xs md:hidden">Supplier *</Label>
                <ParserSupplierSelect
                  value={selectedSupplier?.id || ''}
                  onSelect={(id, name, supplier) => setSelectedSupplier({ id, name, shortName: supplier?.shortName })}
                />
                {/* Show helper under supplier only on mobile; desktop helper rendered in its own row below */}
                <div className="mt-1 text-[11px] text-slate-600 md:hidden">
                  Can't find your portal?{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:underline"
                    onClick={() => setShowPortalModal(true)}
                  >
                    Send request here...
                  </button>
                </div>
              </div>

              {/* Parser Type */}
              <div className="md:col-span-2">
                {/* Fallback label for mobile */}
                <Label className="text-xs md:hidden">Parser Type</Label>
                <div className="h-9 flex items-center">
                  <Badge variant="default" className="text-xs">
                    {selectedSupplier?.name ? `Bot Agent: ${selectedSupplier.name}` : 'Bot Agent'}
                  </Badge>
                </div>
              </div>

              {/* File Upload */}
              <div className="md:col-span-4">
                {/* Fallback label for mobile */}
                <Label className="text-xs md:hidden">Upload Bill *</Label>
                {selectedFile ? (
                  <div className="h-9 flex items-center justify-between gap-2 px-3 border rounded-md bg-green-50 border-green-300">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <File className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{selectedFile.name}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">
                        {(selectedFile.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                      className="h-6 w-6 p-0 flex-shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`h-9 flex items-center justify-center gap-2 px-3 border-2 border-dashed rounded-md cursor-pointer transition-colors ${isDragOver ? 'border-blue-400 bg-blue-50' : 'hover:border-blue-400'}`}
                  >
                    <Upload className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Drop PDF or click</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Action Buttons */}
              <div className="md:col-span-3 flex gap-2 justify-end flex-nowrap items-center self-auto">
                <Button
                  onClick={handleParseData}
                  disabled={!selectedFile || !selectedSupplier || isLoading}
                  className="h-9"
                  size="sm"
                >
                  Parse Data
                </Button>
                <Button
                  onClick={handleClear}
                  variant="outline"
                  disabled={!selectedFile && !parsedData}
                  className="h-9"
                  size="sm"
                >
                  Clear
                </Button>
              </div>

              {/* Row 3 (desktop): helper text aligned under supplier column but not affecting inputs row height */}
              <div className="hidden md:grid md:grid-cols-12 md:col-span-12">
                <div className="md:col-span-3 mt-1 text-[11px] text-slate-600">
                  Can't find your portal?{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:underline"
                    onClick={() => setShowPortalModal(true)}
                  >
                    Send request here...
                  </button>
                </div>
                <div className="md:col-span-2" />
                <div className="md:col-span-4" />
                <div className="md:col-span-3" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 50:50 Layout: PDF Preview + Extracted Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: PDF Preview */}
          <div className="space-y-4">
            {selectedFile ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-slate-700">PDF Preview</h3>
                  <div className="flex items-center gap-2" />
                </div>
                <ReactPDFPreview file={selectedFile} className="shadow-lg min-h-[700px]" />
              </div>
            ) : (
              <Card className="shadow-lg border-2 border-dashed border-slate-300 bg-white/50 min-h-[700px]">
                <CardContent className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <Upload className="h-16 w-16 text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No File Selected</h3>
                  <p className="text-sm text-slate-600">
                    Upload a PDF file to preview it here
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Extracted Data */}
          <div className="space-y-4">
            {canonicalData ? (
              <ExtractedDataDisplay 
                header={canonicalData.header}
                lineItems={canonicalData.lineItems}
                rawData={parsedData}
                onExportCSV={async () => {
                  try {
                    // API expects only the body inside `results`
                    const resultsOnly = (parsedData && (parsedData as any).data && (parsedData as any).data.results)
                      ? (parsedData as any).data.results
                      : ((parsedData && (parsedData as any).results) ? (parsedData as any).results : parsedData);
                    // Skip fields that are null
                    const cleaned = pruneNullsDeep(resultsOnly);
                    const blob = await exportSandboxDataToCsv(cleaned);
                    const filename = `parsed_${selectedSupplier?.name || 'bill'}.csv`;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast({ title: 'CSV exported', description: 'CSV exported successfully' });
                  } catch (error: any) {
                    console.error('[Export CSV] error', error);
                    toast({ title: 'CSV export failed', description: error?.message || 'CSV export failed', variant: 'destructive' });
                  }
                }}
                onFieldUpdate={(field, value) => {
                  setCanonicalData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      header: { ...prev.header, [field]: value }
                    };
                  });
                  toast({ title: 'Field updated', description: 'The value has been updated.' });
                }}
              />
            ) : (
              <Card className="shadow-lg border-2 border-dashed border-slate-300 bg-white/50 min-h-[700px]">
                <CardContent className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="text-6xl mb-4">📊</div>
                  <h3 className="text-lg font-semibold mb-2">No Data Yet</h3>
                  <p className="text-sm text-slate-600">
                    Upload a file and parse it to see extracted data
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      {isLoading && (
        <ParseProgress
          percent={parseProgress.percent}
          stage={parseProgress.stage}
          onCancel={handleCancelParse}
        />
      )}

      {/* Portal Request Modal */}
      <ParserPortalRequestModal open={showPortalModal} onOpenChange={setShowPortalModal} />
    </div>
  );
}
